import { Fragment, useEffect, useState } from 'react'
import { CurecallButton } from '@honestica/curecall-ui'
import { useQuery } from '@tanstack/react-query'
import { ProjectService } from 'src/services/project.service'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { useLocalizedNavigate } from 'src/hooks/use-localized-navigate'
import { RecentImports } from './_components/recent-imports.component'
import FileUploaderComponent from './_components/file-uploader.component'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import PageTitle from 'src/components/page-title/page-title.component'
import Card from 'src/components/card/card.component'

const renderGuideLine = (line: string) => {
  const parts = line.split(/(<bold>.*?<\/bold>)/g)
  return parts.map((part, index) => {
    const match = part.match(/^<bold>(.*)<\/bold>$/)
    return match ? (
      <span key={index} className="c-font-bold">
        {match[1]}
      </span>
    ) : (
      <Fragment key={index}>{part}</Fragment>
    )
  })
}

const GuideStep = ({ index, text }: { index: number; text: string }) => (
  <li className="c-flex c-items-start c-gap-3 c-py-3 first:c-pt-0 c-border-b c-border-grey-200 last:c-border-0">
    <span className="c-flex c-items-center c-justify-center c-shrink-0 c-w-7 c-h-7 c-rounded-full c-bg-primary c-text-white c-text-sm c-font-bold">
      {index}
    </span>
    <p className="c-m-0 c-leading-relaxed">{renderGuideLine(text)}</p>
  </li>
)

const BackToImportMenu = ({ onClick }: { onClick: VoidFunction }) => {
  const { t } = useTranslation()
  return (
    <button className="c-block" onClick={onClick}>
      <a className="iconsminds-arrow-left text-theme-2 c-flex c-items-center c-ml-[-10px] c-mb-4">
        {t('pages.upload.add-another-import')}
      </a>
    </button>
  )
}

export default function UploadPage() {
  const { project } = useCurrentProject()
  const navigate = useLocalizedNavigate()
  const [selectedSlug, setSelectedSlug] = useState<string | undefined>(undefined)
  const [searchParams] = useSearchParams()
  const hasBatchInUrl = searchParams.has('batch')
  const [isResultVisible, setIsResultVisible] = useState(false)
  const { t } = useTranslation()

  const { data: connectors, isLoading } = useQuery({
    queryKey: ['upload-connectors', project.id],
    queryFn: () => ProjectService.getUploadConnectors(project.id),
  })

  useEffect(() => {
    if (connectors && connectors.length === 0) {
      navigate(`/${project.id}/`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectors])

  if (isLoading || !connectors || connectors.length === 0) return <div className="loading" />

  // The batch result doesn't depend on the connector: a URL with a batch goes straight to it
  const showPicker = connectors.length >= 2 && !selectedSlug && !hasBatchInUrl
  const selectedConnector = connectors.find((connector) => connector.slug === selectedSlug) ?? connectors[0]

  if (showPicker) {
    return (
      <div className="c-flex c-flex-col c-items-center">
        <PageTitle>{t('pages.upload.select-type-of-import')}</PageTitle>
        {connectors.map((connector) => (
          <CurecallButton
            key={connector.slug}
            className="c-mb-2"
            onClick={() => setSelectedSlug(connector.slug)}
          >
            {connector.label}
          </CurecallButton>
        ))}
        <RecentImports projectId={project.id} />
      </div>
    )
  }

  return (
    <div>
      {connectors.length >= 2 && !hasBatchInUrl && (
        <BackToImportMenu onClick={() => setSelectedSlug(undefined)} />
      )}
      <h1>
        {hasBatchInUrl && !selectedSlug && connectors.length >= 2
          ? t('pages.upload.import-result')
          : t('pages.upload.import-file', { label: selectedConnector.label })}
      </h1>
      <div className="c-flex c-space-x-8">
        <FileUploaderComponent
          className={selectedConnector.guide && !isResultVisible ? 'c-w-1/2' : 'c-w-full'}
          onResultVisibilityChange={setIsResultVisible}
          project={project}
          accept={selectedConnector.mimeTypePattern?.split('|').join(',')}
          emptyResultMessage={selectedConnector.emptyResultMessage ?? undefined}
          postFunction={(projectId, formData) =>
            ProjectService.postUploadConnectorFile(projectId, selectedConnector.slug, formData)
          }
        />
        {selectedConnector.guide && !isResultVisible && (
          <Card title={selectedConnector.guide.title} className="c-w-1/2">
            <ol className="c-mt-3">
              {selectedConnector.guide.steps.split('\n').map((step, index) => (
                <GuideStep key={index} index={index + 1} text={step} />
              ))}
            </ol>
          </Card>
        )}
      </div>
      {!isResultVisible && <RecentImports projectId={project.id} />}
    </div>
  )
}
